import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { SitePickerBar } from '/src/components/site-picker';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { DashboardPaywall } from '/src/features/_global/paywalls/DashboardPaywall';
import { FEATURES, TIMEZONES } from '/src/features/_global/constants';
import { useMutation, usePermissions, useQueryParams, useSite, useTimeZonedFunctions } from '/src/hooks';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';

import { GoogleAdsAccountSelectionDialog } from '../components/GoogleAdsAccountSelectionDialog';
import { MetaAdsAccountSelectionDialog } from '../components/MetaAdsAccountSelectionDialog';
import { RedditAdsAccountSelectionDialog } from '../components/RedditAdsAccountSelectionDialog';
import { PreviewTopBar } from '../components/PreviewTopBar';
import { initializeMetaAdsConnectionMutation, initializeRedditAdsConnectionMutation } from '../mutations';
import {
  redirectToExternalUrl,
  redirectToGoogleAdsImport,
  redirectToGoogleAdsImportSelection,
  redirectToMetaAdsConnectionSelection,
  redirectToRedditAdsConnectionSelection,
  stripGoogleAdsContinuationParams,
  stripMetaAdsContinuationParams,
  stripRedditAdsContinuationParams,
} from '../navigation';
import { adsLibraryPreviewHeaderQuery } from '../queries';

const DATE_RANGE_PARAM = 'date_range';
const GOOGLE_ADS_ATTEMPT_ID_PARAM = 'google_ads_attempt_id';
const GOOGLE_ADS_CUSTOMERS_PARAM = 'google_ads_customers';
const GOOGLE_ADS_CUSTOMER_IDS_PARAM = 'google_ads_customer_ids';
const GOOGLE_ADS_CONNECT_ERROR_PARAM = 'google_ads_connect_error';
const META_ADS_ATTEMPT_ID_PARAM = 'meta_ads_attempt_id';
const META_ADS_ATTEMPT_TOKEN_PARAM = 'meta_ads_attempt_token';
const META_ADS_ACCOUNTS_PARAM = 'meta_ads_accounts';
const META_ADS_CONNECT_ERROR_PARAM = 'meta_ads_connect_error';
const REDDIT_ADS_ATTEMPT_ID_PARAM = 'reddit_ads_attempt_id';
const REDDIT_ADS_ATTEMPT_TOKEN_PARAM = 'reddit_ads_attempt_token';
const REDDIT_ADS_ACCOUNTS_PARAM = 'reddit_ads_accounts';
const REDDIT_ADS_CONNECT_ERROR_PARAM = 'reddit_ads_connect_error';
const PREVIEW_MAX_ALLOWABLE_RANGE = {
  unit: 'days',
  value: 365,
  maxRange: 365,
};

export function AdsLibraryPreviewPage() {
  const permissions = usePermissions();
  const { allowed: canViewPage, reason } = permissions.can('view', FEATURES.ADS_LIBRARY_PREVIEW);
  const { sites, selectedSite, selectSite, loadingSites } = useSite();
  const { get: getParam, set: setParam, removeAll } = useQueryParams();
  const { getDefaultDateRange, resolveDateRangeParam } = useTimeZonedFunctions(TIMEZONES.UTC);
  const location = useLocation();
  const dateRangeParam = getParam(DATE_RANGE_PARAM);
  const googleAdsAttemptId = getParam(GOOGLE_ADS_ATTEMPT_ID_PARAM);
  const googleAdsCustomersParam = getParam(GOOGLE_ADS_CUSTOMERS_PARAM);
  const googleAdsCustomerIdsParam = getParam(GOOGLE_ADS_CUSTOMER_IDS_PARAM);
  const googleAdsConnectError = getParam(GOOGLE_ADS_CONNECT_ERROR_PARAM);
  const metaAdsAttemptId = getParam(META_ADS_ATTEMPT_ID_PARAM);
  const metaAdsAttemptToken = getParam(META_ADS_ATTEMPT_TOKEN_PARAM);
  const metaAdsAccountsParam = getParam(META_ADS_ACCOUNTS_PARAM);
  const metaAdsConnectError = getParam(META_ADS_CONNECT_ERROR_PARAM);
  const redditAdsAttemptId = getParam(REDDIT_ADS_ATTEMPT_ID_PARAM);
  const redditAdsAttemptToken = getParam(REDDIT_ADS_ATTEMPT_TOKEN_PARAM);
  const redditAdsAccountsParam = getParam(REDDIT_ADS_ACCOUNTS_PARAM);
  const redditAdsConnectError = getParam(REDDIT_ADS_CONNECT_ERROR_PARAM);
  const [featureStartedAt, setFeatureStartedAt] = useState(undefined);
  const [pendingCustomerId, setPendingCustomerId] = useState(null);
  const [pendingMetaAccountId, setPendingMetaAccountId] = useState(null);
  const [pendingRedditAccountId, setPendingRedditAccountId] = useState(null);
  const [metaConnectError, setMetaConnectError] = useState(null);
  const [redditConnectError, setRedditConnectError] = useState(null);
  const initializeMetaConnection = useMutation(initializeMetaAdsConnectionMutation);
  const initializeRedditConnection = useMutation(initializeRedditAdsConnectionMutation);

  useEffect(() => {
    setFeatureStartedAt(undefined);
  }, [selectedSite?.id]);

  useEffect(() => {
    setPendingCustomerId(null);
  }, [googleAdsAttemptId]);

  useEffect(() => {
    setPendingMetaAccountId(null);
  }, [metaAdsAttemptId]);

  useEffect(() => {
    setPendingRedditAccountId(null);
  }, [redditAdsAttemptId]);

  const defaultDateRange = useMemo(
    () =>
      getDefaultDateRange({
        featureStartedAt,
      }),
    [featureStartedAt, getDefaultDateRange],
  );

  const resolvedDateRange = useMemo(
    () =>
      resolveDateRangeParam({
        rawValue: dateRangeParam,
        adapter: JSON_DATE_RANGE_ADAPTER,
        defaultDateRange,
        featureStartedAt,
        maxAllowableRange: PREVIEW_MAX_ALLOWABLE_RANGE,
      }),
    [dateRangeParam, defaultDateRange, featureStartedAt, resolveDateRangeParam],
  );

  useEffect(() => {
    if (!resolvedDateRange?.serializedValue) {
      return;
    }

    if (!dateRangeParam) {
      setParam(DATE_RANGE_PARAM, resolvedDateRange.serializedValue);
      return;
    }

    if (resolvedDateRange.shouldRewrite) {
      setParam(DATE_RANGE_PARAM, resolvedDateRange.serializedValue);
    }
  }, [dateRangeParam, resolvedDateRange?.serializedValue, resolvedDateRange?.shouldRewrite, setParam]);

  const dateRangeConfig = resolvedDateRange?.selectedDateRange ?? defaultDateRange;

  const chooseDateRange = useCallback(
    (value) => {
      const serialized = JSON_DATE_RANGE_ADAPTER.serialize(value);

      if (serialized) {
        setParam(DATE_RANGE_PARAM, serialized);
      }
    },
    [setParam],
  );

  const headerQuery = useQuery({
    ...adsLibraryPreviewHeaderQuery({
      siteId: selectedSite?.id,
    }),
  });

  const connections = useMemo(
    () => headerQuery.data?.adsLibraryPreviewHeader?.connections ?? [],
    [headerQuery.data?.adsLibraryPreviewHeader?.connections],
  );

  const googleAdsCustomers = useMemo(
    () => parseGoogleAdsCustomers(googleAdsCustomersParam, googleAdsCustomerIdsParam),
    [googleAdsCustomerIdsParam, googleAdsCustomersParam],
  );

  const metaAdsAccounts = useMemo(() => parseProviderAccounts(metaAdsAccountsParam), [metaAdsAccountsParam]);
  const redditAdsAccounts = useMemo(() => parseProviderAccounts(redditAdsAccountsParam), [redditAdsAccountsParam]);

  const nextFeatureStartedAt = useMemo(() => getEarliestOldestImportedDataAt(connections), [connections]);

  useEffect(() => {
    setFeatureStartedAt((current) => (current === nextFeatureStartedAt ? current : nextFeatureStartedAt));
  }, [nextFeatureStartedAt]);

  const handleGoogleConnect = useCallback(() => {
    if (!Number.isInteger(selectedSite?.id)) {
      return;
    }

    removeAll([
      GOOGLE_ADS_ATTEMPT_ID_PARAM,
      GOOGLE_ADS_CUSTOMERS_PARAM,
      GOOGLE_ADS_CUSTOMER_IDS_PARAM,
      GOOGLE_ADS_CONNECT_ERROR_PARAM,
    ]);

    redirectToGoogleAdsImport({
      siteId: selectedSite.id,
      pathname: location.pathname,
      search: stripGoogleAdsContinuationParams(location.search),
    });
  }, [location.pathname, location.search, removeAll, selectedSite?.id]);

  const handleMetaConnect = useCallback(() => {
    if (!Number.isInteger(selectedSite?.id)) {
      return;
    }

    setMetaConnectError(null);

    removeAll([
      META_ADS_ATTEMPT_ID_PARAM,
      META_ADS_ATTEMPT_TOKEN_PARAM,
      META_ADS_ACCOUNTS_PARAM,
      META_ADS_CONNECT_ERROR_PARAM,
    ]);

    initializeMetaConnection.mutate(
      {
        siteId: selectedSite.id,
        returnUrl: `${window.SHELL_APP_URL}${location.pathname}${stripMetaAdsContinuationParams(location.search)}`,
      },
      {
        onSuccess: (data) => {
          const redirectUrl = data?.initializeMetaAdsConnection?.redirectUrl;

          if (redirectUrl) {
            redirectToExternalUrl(redirectUrl);
          }
        },
        onError: (error) => {
          setMetaConnectError(error?.graphQLErrors?.[0]?.message || 'Failed to start Meta Ads connection.');
        },
      },
    );
  }, [initializeMetaConnection, location.pathname, location.search, removeAll, selectedSite?.id]);

  const handleRedditConnect = useCallback(() => {
    if (!Number.isInteger(selectedSite?.id)) {
      return;
    }

    setRedditConnectError(null);

    removeAll([
      REDDIT_ADS_ATTEMPT_ID_PARAM,
      REDDIT_ADS_ATTEMPT_TOKEN_PARAM,
      REDDIT_ADS_ACCOUNTS_PARAM,
      REDDIT_ADS_CONNECT_ERROR_PARAM,
    ]);

    initializeRedditConnection.mutate(
      {
        siteId: selectedSite.id,
        returnUrl: `${window.SHELL_APP_URL}${location.pathname}${stripRedditAdsContinuationParams(location.search)}`,
      },
      {
        onSuccess: (data) => {
          const redirectUrl = data?.initializeRedditAdsConnection?.redirectUrl;

          if (redirectUrl) {
            redirectToExternalUrl(redirectUrl);
          }
        },
        onError: (error) => {
          setRedditConnectError(error?.graphQLErrors?.[0]?.message || 'Failed to start Reddit Ads connection.');
        },
      },
    );
  }, [initializeRedditConnection, location.pathname, location.search, removeAll, selectedSite?.id]);

  const dismissGoogleAdsSelection = useCallback(() => {
    setPendingCustomerId(null);
    removeAll([
      GOOGLE_ADS_ATTEMPT_ID_PARAM,
      GOOGLE_ADS_CUSTOMERS_PARAM,
      GOOGLE_ADS_CUSTOMER_IDS_PARAM,
      GOOGLE_ADS_CONNECT_ERROR_PARAM,
    ]);
  }, [removeAll]);

  const handleGoogleAdsCustomerSelect = useCallback(
    (customerId) => {
      if (!googleAdsAttemptId) {
        return;
      }

      setPendingCustomerId(customerId);

      redirectToGoogleAdsImportSelection({
        attemptId: googleAdsAttemptId,
        customerId,
        pathname: location.pathname,
        search: location.search,
      });
    },
    [googleAdsAttemptId, location.pathname, location.search],
  );

  const dismissMetaAdsSelection = useCallback(() => {
    setPendingMetaAccountId(null);
    removeAll([
      META_ADS_ATTEMPT_ID_PARAM,
      META_ADS_ATTEMPT_TOKEN_PARAM,
      META_ADS_ACCOUNTS_PARAM,
      META_ADS_CONNECT_ERROR_PARAM,
    ]);
  }, [removeAll]);

  const handleMetaAdsAccountSelect = useCallback(
    (accountId) => {
      if (!metaAdsAttemptId || !metaAdsAttemptToken) {
        return;
      }

      setPendingMetaAccountId(accountId);

      redirectToMetaAdsConnectionSelection({
        attemptId: metaAdsAttemptId,
        attemptToken: metaAdsAttemptToken,
        accountId,
        pathname: location.pathname,
        search: location.search,
      });
    },
    [location.pathname, location.search, metaAdsAttemptId, metaAdsAttemptToken],
  );

  const dismissRedditAdsSelection = useCallback(() => {
    setPendingRedditAccountId(null);
    removeAll([
      REDDIT_ADS_ATTEMPT_ID_PARAM,
      REDDIT_ADS_ATTEMPT_TOKEN_PARAM,
      REDDIT_ADS_ACCOUNTS_PARAM,
      REDDIT_ADS_CONNECT_ERROR_PARAM,
    ]);
  }, [removeAll]);

  const handleRedditAdsAccountSelect = useCallback(
    (accountId) => {
      if (!redditAdsAttemptId || !redditAdsAttemptToken) {
        return;
      }

      setPendingRedditAccountId(accountId);

      redirectToRedditAdsConnectionSelection({
        attemptId: redditAdsAttemptId,
        attemptToken: redditAdsAttemptToken,
        accountId,
        pathname: location.pathname,
        search: location.search,
      });
    },
    [location.pathname, location.search, redditAdsAttemptId, redditAdsAttemptToken],
  );

  if (!canViewPage) {
    return <DashboardPaywall feature={FEATURES.ADS_LIBRARY_PREVIEW} reason={reason} />;
  }

  return (
    <DashboardPage>
      <SEO title="Ads Library (Preview)" />
      <DashHeader title="Ads Library (Preview)" />
      <PaymentAlerts />
      <SitePickerBar
        sites={sites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        loading={loadingSites}
        addSiteOption={permissions.can('create', FEATURES.SITES).allowed}
        pageDisplayName="Ads Library preview"
      />
      <div className="flex flex-col gap-6 p-10 !pt-5">
        {googleAdsConnectError ? (
          <div
            className="rounded-2xl border border-[#F8D7DA] bg-[#FFF5F5] px-4 py-3 text-sm font-medium text-[#8A2432]"
            data-testid="google-ads-connect-error"
          >
            {googleAdsConnectError}
          </div>
        ) : null}
        {metaAdsConnectError || metaConnectError ? (
          <div
            className="rounded-2xl border border-[#F8D7DA] bg-[#FFF5F5] px-4 py-3 text-sm font-medium text-[#8A2432]"
            data-testid="meta-ads-connect-error"
          >
            {metaAdsConnectError || metaConnectError}
          </div>
        ) : null}
        {redditAdsConnectError || redditConnectError ? (
          <div
            className="rounded-2xl border border-[#F8D7DA] bg-[#FFF5F5] px-4 py-3 text-sm font-medium text-[#8A2432]"
            data-testid="reddit-ads-connect-error"
          >
            {redditAdsConnectError || redditConnectError}
          </div>
        ) : null}
        {selectedSite ? (
          <PreviewTopBar
            selectedSite={selectedSite}
            connections={connections}
            loading={loadingSites || headerQuery.isLoading || headerQuery.isFetching}
            dateRangeConfig={dateRangeConfig}
            resolvedDateRange={resolvedDateRange}
            chooseDateRange={chooseDateRange}
            onGoogleConnect={handleGoogleConnect}
            onMetaConnect={handleMetaConnect}
            onRedditConnect={handleRedditConnect}
            metaConnectLoading={initializeMetaConnection.isLoading || initializeMetaConnection.isPending}
            redditConnectLoading={initializeRedditConnection.isLoading || initializeRedditConnection.isPending}
          />
        ) : null}
      </div>
      {googleAdsAttemptId && googleAdsCustomers.length > 0 ? (
        <GoogleAdsAccountSelectionDialog
          customers={googleAdsCustomers}
          errorMessage={googleAdsConnectError}
          pendingCustomerId={pendingCustomerId}
          onDismiss={dismissGoogleAdsSelection}
          onSelect={handleGoogleAdsCustomerSelect}
        />
      ) : null}
      {metaAdsAttemptId && metaAdsAttemptToken && metaAdsAccounts.length > 0 ? (
        <MetaAdsAccountSelectionDialog
          accounts={metaAdsAccounts}
          errorMessage={metaAdsConnectError}
          pendingAccountId={pendingMetaAccountId}
          onDismiss={dismissMetaAdsSelection}
          onSelect={handleMetaAdsAccountSelect}
        />
      ) : null}
      {redditAdsAttemptId && redditAdsAttemptToken && redditAdsAccounts.length > 0 ? (
        <RedditAdsAccountSelectionDialog
          accounts={redditAdsAccounts}
          errorMessage={redditAdsConnectError}
          pendingAccountId={pendingRedditAccountId}
          onDismiss={dismissRedditAdsSelection}
          onSelect={handleRedditAdsAccountSelect}
        />
      ) : null}
    </DashboardPage>
  );
}

function getEarliestOldestImportedDataAt(connections = []) {
  const timestamps = connections
    .map((connection) => connection.oldestImportedDataAt)
    .filter((value) => Number.isFinite(value));

  if (timestamps.length === 0) {
    return undefined;
  }

  return Math.min(...timestamps);
}

function parseGoogleAdsCustomers(customersValue, legacyCustomerIdsValue) {
  const customers = parseGoogleAdsCustomersValue(customersValue);

  if (customers.length > 0) {
    return customers;
  }

  return parseLegacyGoogleAdsCustomerIds(legacyCustomerIdsValue);
}

function parseGoogleAdsCustomersValue(value) {
  if (typeof value !== 'string' || value === '') {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.flatMap((customer) => {
      if (!customer || typeof customer !== 'object' || Array.isArray(customer)) {
        return [];
      }

      if (typeof customer.id !== 'string' || customer.id === '') {
        return [];
      }

      return [
        {
          id: customer.id,
          name: typeof customer.name === 'string' && customer.name !== '' ? customer.name : null,
        },
      ];
    });
  } catch {
    return [];
  }
}

function parseLegacyGoogleAdsCustomerIds(value) {
  if (typeof value !== 'string' || value === '') {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed)
      ? parsed.flatMap((customerId) =>
          typeof customerId === 'string' && customerId !== '' ? [{ id: customerId, name: null }] : [],
        )
      : [];
  } catch {
    return [];
  }
}

function parseProviderAccounts(value) {
  if (typeof value !== 'string' || value === '') {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.flatMap((account) => {
      if (!account || typeof account !== 'object' || Array.isArray(account)) {
        return [];
      }

      if (typeof account.id !== 'string' || account.id === '') {
        return [];
      }

      return [
        {
          id: account.id,
          name: typeof account.name === 'string' && account.name !== '' ? account.name : null,
        },
      ];
    });
  } catch {
    return [];
  }
}
